import { useEffect, useState } from "react";
import { DropDown } from "../Common/dropDown";
import { InputField } from "../Common/inputField";
import { DatePickerInput } from "../Common/datePicker";
import TrashIcon from "../../assets/trash-outline.svg";
import { CurrencyInputField } from "../Common/currencyInput";
import { PhoneNumberInputField } from "../Common/phoneNumberInput";
import { useAddressLookupStore } from "../../stores/addressLookupStore";
import { formatCanadaPostalCode } from "../../utils/postalCodeFormatter";
import { AddressLookupInput } from "../Common/AddressLookup/addressLookpInput";
import {
  CanadianProvinceCodeEnum,
  CoApplicantIncomeSourceMapping,
  MaritalStatusMapping,
  RelationshipMapping,
} from "../../interfaces/mortgageDetailsInterface";

interface CoApplicantBoxProps {
  onRemove: () => void;
  componentId: string;
  coapplicantNumber: number;
  firstName: string;
  middleName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  postalCode: string;
  province: string;
  email: string;
  phone: string;
  dateOfBirth: Date | null;
  maritalStatus: string;
  relation: string;
  primaryIncomeSource?: string;
  isFirstTimeHomeBuyer: boolean | null;
  annualBasePay: string;
  // totalMonthlyDebt: string;

  firstNameError?: string;
  lastNameError?: string;
  addressError?: string;
  cityError?: string;
  postalCodeError?: string;
  provinceError?: string;
  emailError?: string;
  phoneError?: string;
  dateOfBirthError?: string;
  maritalStatusError?: string;
  relationError?: string;
  primaryIncomeSourceError?: string;
  isFirstTimeHomeBuyerError?: string;
  annualBasePayError?: string;
  // totalMonthlyDebtError?: string;

  setFirstName: (value: string) => void;
  setMiddleName: (value: string) => void;
  setLastName: (value: string) => void;
  setAddress: (value: string) => void;
  setAddress2: (value: string) => void;
  setCity: (value: string) => void;
  setPostalCode: (value: string) => void;
  setProvince: (value: string) => void;
  setEmail: (value: string) => void;
  setPhone: (value: string) => void;
  setDateOfBirth: (value: Date | null) => void;
  setMaritalStatus: (value: string) => void;
  setRelation: (value: string) => void;
  setPrimaryIncomeSource: (value: string) => void;
  setIsFirstTimeHomeBuyer: (value: boolean) => void;
  setAnnualBasePay: (value: string) => void;
  // setTotalMonthlyDebt: (value: string) => void;
}

const CoApplicantBox = ({
  onRemove,
  componentId,
  coapplicantNumber,
  firstName,
  middleName,
  lastName,
  address,
  address2,
  city,
  postalCode,
  province,
  email,
  phone,
  dateOfBirth,
  maritalStatus,
  relation,
  primaryIncomeSource,
  isFirstTimeHomeBuyer,
  annualBasePay,
  // totalMonthlyDebt,

  firstNameError,
  lastNameError,
  addressError,
  cityError,
  postalCodeError,
  provinceError,
  emailError,
  phoneError,
  dateOfBirthError,
  maritalStatusError,
  relationError,
  primaryIncomeSourceError,
  isFirstTimeHomeBuyerError,
  annualBasePayError,
  // totalMonthlyDebtError,

  setFirstName,
  setMiddleName,
  setLastName,
  setAddress,
  setAddress2,
  setCity,
  setPostalCode,
  setProvince,
  setEmail,
  setPhone,
  setDateOfBirth,
  setMaritalStatus,
  setRelation,
  setPrimaryIncomeSource,
  setIsFirstTimeHomeBuyer,
  setAnnualBasePay,
  // setTotalMonthlyDebt,
}: CoApplicantBoxProps) => {
  const { formattedAddresses } = useAddressLookupStore();
  const maritalStatusOptions = Object.keys(MaritalStatusMapping);
  const provinceOptions = Object.values(CanadianProvinceCodeEnum);
  const relationOptions = Object.keys(RelationshipMapping);
  const coApplicantSourceOfIncomeOptions = Object.keys(
    CoApplicantIncomeSourceMapping
  );

  const [manualEdit, setManualEdit] = useState(false);

  useEffect(() => {
    const formattedAddress = formattedAddresses[componentId];

    if (formattedAddress && !manualEdit) {
      if (address !== formattedAddress.address1) {
        setAddress(formattedAddress.address1);
      }
      if (city !== formattedAddress.city) {
        setCity(formattedAddress.city);
      }
      if (postalCode !== formattedAddress.postalCode) {
        setPostalCode(formattedAddress.postalCode);
      }
      if (province !== formattedAddress.state) {
        setProvince(formattedAddress.state);
      }
    }
  }, [
    formattedAddresses,
    componentId,
    address,
    city,
    postalCode,
    province,
    setAddress,
    setCity,
    setPostalCode,
    setProvince,
    manualEdit,
  ]);

  const handleAddressChange = (value: string) => {
    setManualEdit(true);
    setAddress(value);
  };

  const handleCityChange = (value: string) => {
    setManualEdit(true);
    setCity(value);
  };

  const handlePostalCodeChange = (value: string) => {
    setManualEdit(true);
    setPostalCode(value);
  };

  const handleProvinceChange = (value: string) => {
    setManualEdit(true);
    setProvince(value);
  };
  const handlePhoneChange = (rawValue: string) => {
    setPhone(rawValue);
  };
  return (
    <div className="rounded-[8px] border-[#E5E7EB] border-2">
      <div className="font-medium flex items-center justify-between w-full bg-[#E5E7EB]/50 rounded-t-[8px] border-b-[#E5E7EB] border-b-2 px-3 py-4">
        <div>Co-Applicant {coapplicantNumber}</div>
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-medium"
          onClick={onRemove}
        >
          Delete <img src={TrashIcon} alt="" />
        </button>
      </div>
      <div className="p-4 space-y-6">
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="w-full space-y-1">
            <InputField
              id="firstName"
              label="Legal First Name*"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {firstNameError && (
              <p className="text-red-600 text-sm">{firstNameError}</p>
            )}
          </div>

          <InputField
            id="middleName"
            label="Middle Name"
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
          />
          <div className="w-full space-y-1">
            <InputField
              id="lastName"
              label="Legal Last Name*"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            {lastNameError && (
              <p className="text-red-600 text-sm">{lastNameError}</p>
            )}
          </div>
        </div>

        <AddressLookupInput type={componentId} />

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full space-y-1">
            <InputField
              id="address"
              label="Address*"
              value={address}
              onChange={(e) => handleAddressChange(e.target.value)}
            />
            {addressError && (
              <p className="text-red-600 text-sm">{addressError}</p>
            )}
          </div>
          <InputField
            id="address2"
            label="Address Line 2"
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full">
            <div className="w-full space-y-1">
              <InputField
                id="city"
                label="City*"
                value={city}
                onChange={(e) => handleCityChange(e.target.value)}
              />
              {cityError && <p className="text-red-600 text-sm">{cityError}</p>}
            </div>
          </div>
          <div className="w-full grid grid-cols-2 gap-4">
            <div className="w-full space-y-1">
              <InputField
                id="postalCode"
                label="Postal Code*"
                value={formatCanadaPostalCode(postalCode)}
                onChange={(e) => handlePostalCodeChange(e.target.value)}
              />
              {postalCodeError && (
                <p className="text-red-600 text-sm">{postalCodeError}</p>
              )}
            </div>
            <div className="w-full space-y-1">
              <DropDown
                label="Province*"
                options={provinceOptions}
                selectedOption={province || ""}
                onSelect={(value) => handleProvinceChange(value)}
              />
              {provinceError && (
                <p className="text-red-600 text-sm">{provinceError}</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full space-y-1">
            <InputField
              id="email"
              label="Email*"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
            />
            {emailError && <p className="text-red-600 text-sm">{emailError}</p>}
          </div>

          <div className="w-full space-y-1">
            <PhoneNumberInputField
              id="phone"
              label="Phone number*"
              value={phone}
              onChange={handlePhoneChange}
            />
            {phoneError && <p className="text-red-600 text-sm">{phoneError}</p>}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full space-y-1">
            <DropDown
              label="Marital Status*"
              options={maritalStatusOptions}
              selectedOption={
                Object.keys(MaritalStatusMapping).find(
                  (key) => MaritalStatusMapping[key] === maritalStatus
                ) || ""
              }
              onSelect={(value) =>
                setMaritalStatus(MaritalStatusMapping[value])
              }
            />
            {maritalStatusError && (
              <p className="text-red-600 text-sm">{maritalStatusError}</p>
            )}
          </div>
          <div className="w-full space-y-1">
            <DatePickerInput
              label="Date of Birth*"
              selectedDate={dateOfBirth}
              onDateChange={setDateOfBirth}
              disableFuture={true}
            />
            {dateOfBirthError && (
              <p className="text-red-600 text-sm">{dateOfBirthError}</p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full space-y-1">
            <DropDown
              label="What is your Co-Applicant's primary source of Income?"
              options={coApplicantSourceOfIncomeOptions}
              selectedOption={
                Object.keys(CoApplicantIncomeSourceMapping).find(
                  (key) =>
                    CoApplicantIncomeSourceMapping[key] === primaryIncomeSource
                ) || ""
              }
              onSelect={(value) =>
                setPrimaryIncomeSource(CoApplicantIncomeSourceMapping[value])
              }
            />
            {primaryIncomeSourceError && (
              <p className="text-red-600 text-sm">{primaryIncomeSourceError}</p>
            )}
          </div>

          <div>
            <CurrencyInputField
              id="annualBasePay"
              label="Co-Applicant Annual Base Pay*"
              value={annualBasePay}
              onChange={setAnnualBasePay}
            />
            {annualBasePayError && (
              <p className="text-red-600 text-sm">{annualBasePayError}</p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full space-y-1">
            <DropDown
              label="Relation to Co-Applicant*"
              options={relationOptions}
              selectedOption={
                Object.keys(RelationshipMapping).find(
                  (key) => RelationshipMapping[key] === relation
                ) || ""
              }
              onSelect={(value) => setRelation(RelationshipMapping[value])}
            />
            {relationError && (
              <p className="text-red-600 text-sm">{relationError}</p>
            )}
          </div>
          <div className="w-full space-y-1">
            <DropDown
              label="Are they a first-time home buyer?*"
              options={["Yes", "No"]}
              selectedOption={
                isFirstTimeHomeBuyer === null
                  ? ""
                  : isFirstTimeHomeBuyer
                    ? "Yes"
                    : "No"
              }
              onSelect={(value) => {
                setIsFirstTimeHomeBuyer(
                  value === "Yes" ? true : value === "No" && false
                );
              }}
            />
            {isFirstTimeHomeBuyerError && (
              <p className="text-red-600 text-sm">
                {isFirstTimeHomeBuyerError}
              </p>
            )}
          </div>
        </div>
        {/* <div className="grid lg:grid-cols-2 gap-4">
          <div className="w-full space-y-1">
            <CurrencyInputField
              // id="totalMonthlyDebt"
              label="Total Monthly Debt*"
              // value={totalMonthlyDebt}
              // onChange={setTotalMonthlyDebt}
            />
            {totalMonthlyDebtError && (
              <p className="text-red-600 text-sm">{totalMonthlyDebtError}</p>
            )}
          </div>
        </div> */}
      </div>
    </div>
  );
};

export { CoApplicantBox };
